import $ from 'jquery';
import { queryString, redirectWithParams } from '@/js/modules/query_params';
import {
  CLEAR_SEARCH_BUTTON,
  INLINE_SELECTIZE,
  SHOW_ALL_OPTION,
} from '@/js/modules/selectize_plugins';
import { onPageLoad } from '@/js/modules/page_load';

onPageLoad(() => {
  const dropdownFilterAjax = ($selector) => {
    if ($selector.length === 0) {
      return;
    }

    const plugins = [INLINE_SELECTIZE, CLEAR_SEARCH_BUTTON, SHOW_ALL_OPTION];

    $selector.each(function () {
      const type = $(this).data('param');
      const withProjects =
        $(this).data('withProjects') === true || $(this).data('withProjects') === 'true';

      if (!type) {
        console.error('Please add data-param to select element');
        return;
      }

      const config = getDropdownConfig(type, withProjects);

      $(this).selectize({
        plugins,
        maxOptions: null,
        maxItems: 1,
        persist: true,
        placeholder: config.placeholder,
        valueField: 'id',
        labelField: config.labelField,
        searchField: config.searchField,
        preload: false,
        load(query, callback) {
          this.lastQuery = query;
          loadDropdownData({
            type,
            withProjects,
            query,
            page: 1,
            append: false,
            callback: (results) => {
              const processedResults = results.map((item) => ({
                ...item,
                ...(isClientType(type) && { display_name: item.name || item.email }),
              }));
              callback(processedResults);
            },
            selectizedInstance: this,
          });
        },
        render: {
          item(item) {
            const displayText = getDisplayText(item, type);
            return `<div class="field__tag-item">${displayText}</div>`;
          },
          option(item) {
            const displayText = getDisplayText(item, type);
            return `<div class="option">${displayText}</div>`;
          },
        },
        onDropdownOpen($dropdown) {
          let currentPage = 1;
          let isLoadingMore = false;
          let hasMorePages = true;
          const dropdownContent = $dropdown[0].querySelector('.selectize-dropdown-content');

          if (dropdownContent && !dropdownContent.dataset.scroll) {
            dropdownContent.dataset.scroll = 'true';

            dropdownContent.onscroll = async () => {
              const nearBottom =
                dropdownContent.scrollTop + dropdownContent.clientHeight >=
                dropdownContent.scrollHeight - 50;

              if (nearBottom && !isLoadingMore && hasMorePages) {
                isLoadingMore = true;
                currentPage += 1;

                await loadDropdownData({
                  type,
                  withProjects,
                  query: this.lastQuery || '',
                  page: currentPage,
                  append: true,
                  selectizedInstance: this,
                  onComplete: (result, pagination) => {
                    isLoadingMore = false;
                    if (!result || result.length === 0 || !pagination || !pagination.next) {
                      hasMorePages = false;
                    }
                  },
                });
              }
            };
          }

          if (!this.hasLoadedOnce) {
            this.hasLoadedOnce = true;
            loadDropdownData({
              type,
              withProjects,
              query: '',
              page: 1,
              append: false,
              selectizedInstance: this,
              onComplete: (result, pagination) => {
                hasMorePages = result && result.length > 0 && pagination && pagination.next;
              },
            });
          }
        },
        onChange(value) {
          const $filterElement = this.$input;

          if (!$filterElement.parents('.filter-list__item').hasClass('filter-list__item--open')) {
            this.clear();
            return false;
          }

          const params = queryString();
          const param = config.paramName;
          let paramValue = value;
          if (type === 'vendor' && value && value !== 'all') {
            const selectedOption = this.options[value];
            paramValue = selectedOption ? selectedOption.name : value;
          }

          params[param] = paramValue;

          if (!params[param]) {
            return false;
          }
          if (params[param] === 'all') {
            delete params[param];
          }

          redirectWithParams(Object.assign(params));
        },
      });

      // Needed so we can check if selectize is initialized
      this.dataset.initialized = true;
    });
  };

  const isClientType = (type) => {
    return type === 'client_id' || type === 'client_ids';
  };

  const getDropdownConfig = (type, withProjects = false) => {
    const configs = {
      client_id: {
        placeholder: 'Search for a client...',
        labelField: 'display_name',
        searchField: ['name', 'email'],
        paramName: 'client_id',
        endpoint: '/dropdowns/clients',
        collection: 'clients',
        withProjects, // this gets only clients that have projects (if true)
      },
      client_ids: {
        placeholder: 'Search for a client...',
        labelField: 'display_name',
        searchField: ['name', 'email'],
        paramName: 'client_ids',
        endpoint: '/dropdowns/clients',
        collection: 'clients',
        withProjects, // this gets only clients that have projects (if true)
      },
      project_id: {
        placeholder: 'Search for a project...',
        labelField: 'name',
        searchField: ['name'],
        paramName: 'project_id',
        endpoint: '/dropdowns/projects',
        collection: 'projects',
        withProjects: false,
      },
      project_ids: {
        placeholder: 'Search for a project...',
        labelField: 'name',
        searchField: ['name'],
        paramName: 'project_ids',
        endpoint: '/dropdowns/projects',
        collection: 'projects',
        withProjects: false,
      },
      vendor: {
        placeholder: 'Search for a vendor...',
        labelField: 'name',
        searchField: ['name'],
        paramName: 'vendor',
        endpoint: '/dropdowns/item_vendors',
        collection: 'vendors',
        withProjects: false,
      },
    };

    return configs[type] || null;
  };

  const getDisplayText = (item, type) => {
    return isClientType(type) ? item.name || item.email : item.name;
  };

  const loadDropdownData = ({
    type,
    withProjects = false,
    query = '',
    page = 1,
    append = false,
    callback,
    selectizedInstance,
    onComplete,
  } = {}) => {
    const config = getDropdownConfig(type, withProjects);
    const input = selectizedInstance.$input[0];
    const projectId = input.dataset.projectId;
    const scope = input.dataset.scope;

    const params = new URLSearchParams({ search: query, page });

    if (config.withProjects) {
      params.append('with_projects', 'true');
    }

    if (scope) {
      params.append('scope', scope);
    }
    if (projectId) {
      params.append('project_id', projectId);
    }

    fetch(`${config.endpoint}?${params.toString()}`, { headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((data) => {
        selectizedInstance.settings._pagination = data.pagination;

        const items = data[config.collection];
        items.forEach((item) => {
          selectizedInstance.addOption(item);
        });

        selectizedInstance.refreshOptions(!append);

        if (callback) {
          callback(items);
        }
        if (onComplete) {
          onComplete(items, data.pagination);
        }
      })
      .catch(() => {
        if (callback) {
          callback([]);
        }
        if (onComplete) {
          onComplete(null, null);
        }
      });
  };

  const initDropdownFilterAjax = () => {
    const $dropdowns = $('select.filter-dropdown__select-ajax').filter(function () {
      return this.dataset.initialized !== 'true';
    });

    if ($dropdowns.length > 0) {
      dropdownFilterAjax($dropdowns);
    }
  };

  $(document).ready(function () {
    initDropdownFilterAjax();
  });
});
